import { updatePageProps } from '@eventbrite/datalayer-library';
import { getWindowObject } from '@eventbrite/feature-detection';
import {
    hasUrgencySignalsEnabled,
    UrgencySignalsProps,
} from '@eventbrite/urgency-signals';
import type { EventDescriptionProps } from '../components/EventDetails/EventDescription';
import {
    CheckoutWidgetProps,
    EventDetailsProps,
    EventHeroScopedProps,
    LegacyConversionBarProps,
    Settings,
    User,
    type EventListingsResponse,
    type ServerEvent,
} from '../types';
import { isRepeatingEvent } from '../utils';
import {
    checkExpressCheckoutCompatibility,
    getFaqsData,
    getParkingInfo,
    getRefundPolicy,
} from './utils';

export const getHeapUpdatePropsObject = () => {
    const dataLayer = getWindowObject('dataLayer');
    return dataLayer
        ? dataLayer.find((item) => item.event == 'Update Page Props')?.page
        : {};
};

const hasWidgetType = (
    widgetType: String,
    eventDescription?: EventDescriptionProps,
) => {
    return eventDescription?.structuredContent?.widgets.some(
        (widget) => widget.type === widgetType,
    );
};

const hasVideoModule = (eventDescription?: EventDescriptionProps): Boolean => {
    return !!eventDescription?.structuredContent?.modules.find(
        (module) => module.type === 'video',
    );
};

const normalizeCategory = (category: string) => {
    return category.toLocaleLowerCase().replace(/\s/g, '_');
};

const hasImagesInDescription = (
    eventDescription?: EventDescriptionProps,
): boolean => {
    return !!eventDescription?.structuredContent?.modules.find(
        (module) => module.type === 'image',
    );
};

const hasHeroImages = (eventHero?: EventHeroScopedProps): boolean => {
    const { featuredVideo, items = [] } = eventHero || {};
    const hasVideo = !!featuredVideo?.url;
    const hasAtLeastOneImage = items.length > 0;
    const hasMoreThanOneImage = items.length > 1;
    return (
        (hasVideo && hasMoreThanOneImage) || (!hasVideo && hasAtLeastOneImage)
    );
};

const getVideoSource = (
    hasFeaturedVideo: boolean,
    hasNativeVideo: boolean,
): 'url' | 'service' | null => {
    if (hasNativeVideo) {
        return 'service';
    }

    if (hasFeaturedVideo) {
        return 'url';
    }

    return null;
};

export const dataLayerMiddleware = (
    locale: string,
    event?: ServerEvent,
    components?: {
        eventDescription?: EventDescriptionProps;
        eventDetails?: EventDetailsProps;
        eventHero?: EventHeroScopedProps;
        checkoutWidget?: CheckoutWidgetProps;
        conversionBar?: LegacyConversionBarProps;
    },
    urgencySignals?: UrgencySignalsProps,
    user?: User,
    eventListingResponse?: EventListingsResponse,
    settings?: Settings,
) => {
    const eventDescription = components?.eventDescription;
    const eventDetails = components?.eventDetails;
    const eventHero = components?.eventHero;
    const hasHeroVideo = !!components?.eventHero?.featuredVideo?.url;
    const hasNativeVideo = !!eventListingResponse?.video;

    const isPaid = eventDetails?.refund?.isPaidEvent || false;
    const shouldShowExpressCheckout =
        event && checkExpressCheckoutCompatibility(event, isPaid);

    if ((event?.isProtected as any) === 'isRepeatingEvent') {
        console.error('isProtected is not a boolean', event);
    }

    const page = {
        category: event?.category ? normalizeCategory(event.category) : '',
        eventEndDate: event?.end.utc,
        eventId: eventListingResponse?.parentEventId ?? event?.id,
        childEventId: eventListingResponse?.parentEventId
            ? event?.id
            : undefined,
        eventStartDate: event?.start.utc,
        eventStatus: event?.salesStatus.sales_status,
        faqsNumber: getFaqsData(eventDescription)?.length,
        format: event?.format ? normalizeCategory(event.format) : '',
        has2x1: event?.ticketsInfo?.hasBogoTickets,
        hasArtistWidget: hasWidgetType('lineup', eventDescription),
        hasAgenda: hasWidgetType('agenda', eventDescription),
        hasAutogeneratedContent:
            !!eventDescription?.structuredContent?.hasAutogeneratedContent,
        hasDescriptionImages: hasImagesInDescription(eventDescription),
        hasEarlyBirdDiscount: urgencySignals?.categories.earlyBird,
        hasFaqs: hasWidgetType('faqs', eventDescription),
        hasFewTicketsLeftUrgencySignal: urgencySignals?.messages.fewTickets,
        hasGoingFastUrgencySignal: urgencySignals?.messages.goingFast,
        hasHeroImages: hasHeroImages(components?.eventHero),
        hasImages: eventHero?.items?.length,
        hasNewUrgencySignal: urgencySignals?.categories.new,
        hasOpenDiscount: event?.discount?.discountType === 'open',
        hasPrivateDiscount: event?.discount?.discountType === 'coded',
        hasPopularUrgencySignal: urgencySignals?.categories.popular,
        hasRSVP: event?.shouldDisplayRsvp,
        hasSalesEndSoonUrgencySignal: urgencySignals?.messages.salesEndSoon,
        hasUrgencySignals: hasUrgencySignalsEnabled(urgencySignals),
        hasVideo:
            hasVideoModule(eventDescription) || hasHeroVideo || hasNativeVideo,
        heroVideo: hasHeroVideo || hasNativeVideo,
        isAuthenticated: user?.isAuthenticated,
        isFree: !isPaid,
        isPaid,
        isListed: event?.isListed,
        isLive: event?.isLive,
        isOnline: event?.isOnlineEvent,
        isPrivate: event?.isProtected || false,
        isRepeatingEvent:
            event &&
            isRepeatingEvent({
                event,
                event_listing_response: eventListingResponse,
            }),
        isSeriesParent: event?.isSeriesParent,
        isSeriesInfiniteCalendar:
            event &&
            isRepeatingEvent({
                event,
                event_listing_response: eventListingResponse,
            }) &&
            settings?.featureFlags?.seriesInfiniteCalendar,
        multiticket: false,
        subcategory: event?.subcategory
            ? normalizeCategory(event.subcategory)
            : '',
        timedEntry: !!eventListingResponse?.eventSessions?.length,
        videoSource: getVideoSource(hasHeroVideo, hasNativeVideo),
        locale: locale,
        goodToKnowAge: !!event?.ageRestriction,
        goodToKnowCheckin: !!components?.eventDetails?.dates.doorTime,
        goodToKnowParking: eventListingResponse
            ? getParkingInfo(eventListingResponse.highlights)
            : null,
        goodToKnowFAQNumber: getFaqsData(components?.eventDescription).length,
        eventRefund: getRefundPolicy(eventListingResponse?.refunds),
        shouldShowExpressCheckout,
        hasExternalTickets:
            !!components?.conversionBar?.externalTicketsAreAvailable,
        listingSource: 'web:legacy',
    };

    updatePageProps({ page });
};
